import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './Panel.scss';

import FinancialSummary from './components/FinancialSummary/FinancialSummary';
import PendingTable from './components/Pending/PendingTable';
import MainCards from './components/MainCards/MainCards';
import { FaListCheck, FaCashRegister, FaArrowsRotate } from 'react-icons/fa6';
import { Spinner } from 'react-bootstrap';
// API calls
import { getRealTimeSalesStatisticsByLocalId } from '../../../../crud/management_api/sales_statistics';
import { getLastLocalSessionByLocalId, getAllPendings } from '../../../../crud/management_api/local_sessions';
import { getPermissionState } from 'utils/functions';
// Loading spinner
import MiniLoadingSpinner from '../../../../components/Spinners/MiniLoadingSpinner';
import WaitersStats from './components/WaitersStats/WaitersStats';
import IndividualCaja from '../../../Restaurant/Caja/CajasManagement/components/IndividualCaja/IndividualCaja';
import { getLocalCajaManagementByLocalId } from '../../../../crud/management_api/locales_caja_management';

export interface ReferenceValues {
  sales: {
    daily_variation: number | null;
    weekly_variation: number | null;
    today_sales_restaurant: number | null;
    yesterday_variation: number | null;
    last_week_variation: number | null;
    yesterday_sales: number | null;
    last_week_sales: number | null;
    daily_average: number | null;
    weekly_average: number | null;
  };
  tips: {
    daily_variation: number | null;
    weekly_variation: number | null;
    yesterday_variation: number | null;
    last_week_variation: number | null;
    yesterday_tip: number | null;
    last_week_tip: number | null;
    daily_average: number | null;
    weekly_average: number | null;
  };
}

export interface UserStats {
  name: string;
  total: number;
  count: number;
  avg_total: number;
  tip: number;
  avg_tip: number;
  cancelled: number;
  cancelled_amount: number;
  avg_cancelled: number;
  avg_tip_variation: number;
}

export interface TypeStats {
  name: string;
  total: number;
  count: number;
  avg_total: number;
}

export interface SalesStatistics {
  total_charged: number;
  gross_sales: number;
  valid_receipt_count: number;
  users_count: number;
  net_sales: number;
  pending_payments_count: number;
  total_pending: number;
  gross_loss: number;
  net_loss: number;
  cancelled_receipt_count: number;
  avg_ticket: number;
  users_stats: { [userId: string]: UserStats };
  type_stats: { [typeId: string]: TypeStats };
  total_tip: number;
  avg_tip_by_ticket: number;
  total_discounts: number;
  total_coupons: number;
  avg_tip_by_user: number;
  referenceValues: ReferenceValues;
}

const Panel: React.FC = () => {
  const [stats, setStats] = useState<SalesStatistics | null>(null);
  const [session, setSession] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [cajas, setCajas] = useState<any[]>([]);
  const [actionCaja, setActioncaja] = useState<boolean>(false);
  const [pendingsData, setPendingsData] = useState<any | null>(null);
  const [refreshingPendings, setRefreshingPendings] = useState<boolean>(false);
  const navigate = useNavigate();


  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user ? Number(user.local_id) : 0;

    // Fetch cajas for the current local
    getLocalCajaManagementByLocalId(localId)
      .then((response: any) => {
        setCajas(response.data);
      })
      .catch((error: any) => {
        console.error('Error obteniendo cajas:', error);
        toast.error('Error cargando cajas');
      });
  }, []);

  // Nuevo efecto para refrescar datos cuando se cierra una caja
  useEffect(() => {
    if (!actionCaja) return;
    setLoading(true);
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user ? Number(user.local_id) : 0;
    // Actualizar cajas
    getLocalCajaManagementByLocalId(localId)
      .then((response: any) => {
        setCajas(response.data);
      })
      .catch((error: any) => {
        console.error('Error obteniendo cajas:', error);
        toast.error('Error cargando cajas');
      });
    // Actualizar sesión y stats
    getLastLocalSessionByLocalId(localId)
      .then((response: any) => {
        setSession(response.data);
        return getRealTimeSalesStatisticsByLocalId(localId);
      })
      .then((response: any) => {
        setStats(response.data);
      })
      .catch((error: any) => {
        console.error('Error refrescando datos del panel:', error);
        toast.error('Error refrescando datos del panel');
      })
      .finally(() => {
        setLoading(false);
        setActioncaja(false);
      });
  }, [actionCaja]);

  useEffect(() => {
    setLoading(true);
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user ? Number(user.local_id) : 0;

    // 1) fetch last session
    getLastLocalSessionByLocalId(localId)
      .then((response) => {
        setSession(response.data);
        // 2) fetch real-time sales stats
        return getRealTimeSalesStatisticsByLocalId(localId);
      })
      .then((response) => {
        setStats(response.data);
      })
      .catch((error) => {
        console.error('Error obteniendo datos de panel:', error);
        toast.error('Error cargando datos del panel');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  // Fetch pendings separately - this will be used by both PendingTable and IndividualCaja
  useEffect(() => {
    if (!session?.local_session_id) {
      setPendingsData(null);
      return;
    }

    getAllPendings(session.local_session_id)
      .then((response) => {
        setPendingsData(response.data);
      })
      .catch((error) => {
        console.error('Error obteniendo pendientes:', error);
        setPendingsData(null);
      });
  }, [session?.local_session_id, actionCaja]); // Re-fetch when actionCaja changes

  // Manual refresh function for pendings
  const handleRefreshPendings = () => {
    if (!session?.local_session_id) {
      toast.warning('No hay una sesión activa');
      return;
    }

    setRefreshingPendings(true);
    getAllPendings(session.local_session_id)
      .then((response) => {
        setPendingsData(response.data);
        toast.success('Pendientes actualizados');
      })
      .catch((error) => {
        console.error('Error actualizando pendientes:', error);
        toast.error('Error actualizando pendientes');
      })
      .finally(() => {
        setRefreshingPendings(false);
      });
  };


useEffect(() => {
    const seeSales = getPermissionState(4); // forzar recálculo del menú al montar el componente
    if (seeSales === false) {
      navigate('/App/Restaurant');
    }
  }, [navigate]);

  const renderCajas = () => {
    // Contar cuántas cajas están abiertas
    const openCajasCount = cajas.filter(c => c.is_open).length;
    
    return (
      <div className="cajas-container">
        {cajas.length === 0 ? (
          <div className="no-cajas-message">
            ¡No hay cajas abiertas! Para tomar pedidos debe abrir al menos una.
          </div>
        ) : (
          cajas.map((caja, idx) => (
          <IndividualCaja
             key={idx}
             caja={caja}
             cajas={cajas}
             setCajas={setCajas}
             setActioncaja={setActioncaja}
             isSessionActive={session?.active || false}
             hasPendientes={
               !!(pendingsData && (pendingsData?.pending_pedidos?.length > 0 || pendingsData?.pending_receipts?.length > 0))
             }
             openCajasCount={openCajasCount}
           />
         ))
        )}
      </div>
    );
  };

  if (loading) {
    return <MiniLoadingSpinner />;
  }

  return (
    <div className="panel-container">
      {/* ─── Shortcuts bar (positioned at top) ───────────────── */}
      <MainCards session={session} />
      
      {/* ─── Top-row: Financial Summary & Pending ────────────────── */}
      {session && (
        <div className="panel-cards-row">
          {/* Financial Summary - replaces Sales, Tips, ExtraStats */}
          <div className="card-wrapper financial-wrapper">
            <FinancialSummary data={stats} />
          </div>

          {/* Pending Actions card */}
          <div className="card-wrapper pending-card">
            <div className="pending-header-wrapper">
              <h3 className="pending-title">
                <FaListCheck className='pending-title-icon'/>
                Acciones pendientes
              </h3>
              <button 
                className="pending-refresh-btn"
                onClick={handleRefreshPendings}
                disabled={refreshingPendings}
                title="Actualizar pendientes"
              >
                {refreshingPendings ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FaArrowsRotate className={refreshingPendings ? 'spinning' : ''} />
                )}
              </button>
            </div>
            <PendingTable 
              localSessionId={session.local_session_id}
              setActioncaja={setActioncaja}
              pendingsData={pendingsData}
              setPendingsData={setPendingsData}
            />
          </div>
          <div className='card-wrapper waiters-wrapper'>
            {stats && (
              <div className='card-waiters-stats'>
                <WaitersStats data={stats} />
              </div>
            )}
            {/* Agregado: renderCajas para mostrar las cajas en el panel */}
            {session && (
              <div className='card-cajas-panel'>
                <div className='card-header'>
                  <h3 className='cajas-title'> <FaCashRegister/>Cajas</h3>
                </div>
                {renderCajas && typeof renderCajas === 'function' ? renderCajas() : null}
              </div>
            )}
          </div>

        </div>
      )}

      {/* ─── If no active session, show welcome message ───────────────── */}
      {!session && (
        <div className="welcome-message">
          <h2>¡Bienvenido a Medio Punto!</h2>
          <p>
            Actualmente no hay una sesión activa.
            <br />
            Para comenzar, ¡inicia sesión!
          </p>
        </div>
      )}
    </div>
  );
};

export default Panel;
